import { Box, Grid, Typography } from "@mui/material";
import { Select, TextField } from "components/UI";
import { ADDRESSES } from "features/members";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";

const StructureDetailAddressSection = memo(
  ({
    register,
    setValue,
    watch,
    errors,
    countriesQuery,
    regionsQuery,
    zonesQuery,
    citiesQuery,
    subcitiesQuery,
    woredasQuery,
    placesRef,
    isCreatedByUser
  }) => {
    const { t } = useTranslation();

    const handleRegionChange = useCallback(
      e => {
        setValue("region_id", e.target.value, {
          shouldValidate: true
        });

        if (e.target.value === ADDRESSES.ADDIS_ABABA.REGION_ID) {
          setValue("zone_id", ADDRESSES.ADDIS_ABABA.ZONE_ID, {
            shouldValidate: true
          });
          setValue("city_id", ADDRESSES.ADDIS_ABABA.CITY_ID, {
            shouldValidate: true
          });
        } else {
          setValue("zone_id", "", {
            shouldValidate: true
          });
          setValue("city_id", "", {
            shouldValidate: true
          });
        }
      },
      [setValue]
    );

    const isAddisAbaba = watch("region_id") === ADDRESSES.ADDIS_ABABA.REGION_ID;

    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {t("denominations.structures.structureAddress")}
        </Typography>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={3}>
            <Select
              isLabelBold={1}
              label={t("form.common.country")}
              name="country_id"
              options={
                countriesQuery.data?.result?.map(country => ({
                  optionLabel: country?.name,
                  value: country?.id
                })) || []
              }
              register={register}
              error={!!errors.country_id?.message}
              helperText={errors.country_id?.message}
              disabled={!isCreatedByUser}
            />
          </Grid>
          <Grid item xs={3}>
            <Select
              isLabelBold={1}
              label={t("form.common.region")}
              name="region_id"
              options={
                regionsQuery.data?.result?.map(region => ({
                  optionLabel: region?.name,
                  value: region?.id
                })) || []
              }
              register={register}
              error={!!errors.region_id?.message}
              helperText={errors.region_id?.message}
              onChange={handleRegionChange}
              disabled={!isCreatedByUser}
            />
          </Grid>
          {!isAddisAbaba && (
            <>
              <Grid item xs={3}>
                <Select
                  isLabelBold={1}
                  label={t("form.common.zone")}
                  name="zone_id"
                  options={
                    zonesQuery.data?.result?.map(zone => ({
                      optionLabel: zone?.name,
                      value: zone?.id
                    })) || []
                  }
                  register={register}
                  error={!!errors.zone_id?.message}
                  helperText={errors.zone_id?.message}
                  disabled={isAddisAbaba || !isCreatedByUser}
                />
              </Grid>
              <Grid item xs={3}>
                <Select
                  isLabelBold={1}
                  label={t("form.common.city")}
                  name="city_id"
                  options={
                    citiesQuery.data?.result?.map(city => ({
                      optionLabel: city?.name,
                      value: city?.id
                    })) || []
                  }
                  register={register}
                  error={!!errors.city_id?.message}
                  helperText={errors.city_id?.message}
                  disabled={isAddisAbaba || !isCreatedByUser}
                />
              </Grid>
            </>
          )}
          <Grid item xs={3}>
            <Select
              isLabelBold={1}
              label={t("form.common.subcity")}
              name="sub_city_id"
              options={
                subcitiesQuery.data?.result?.map(subcity => ({
                  optionLabel: subcity?.name,
                  value: subcity?.id
                })) || []
              }
              register={register}
              error={!!errors.sub_city_id?.message}
              helperText={errors.sub_city_id?.message}
              disabled={!isCreatedByUser}
            />
          </Grid>
          <Grid item xs={3}>
            <Select
              isLabelBold={1}
              label={t("form.common.woreda")}
              name="woreda_id"
              options={
                woredasQuery.data?.result?.map(woreda => ({
                  optionLabel: woreda?.name,
                  value: woreda?.id
                })) || []
              }
              register={register}
              error={!!errors.woreda_id?.message}
              helperText={errors.woreda_id?.message}
              disabled={!isCreatedByUser}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label={t("form.common.kebele")}
              name="kebele"
              error={!!errors.kebele?.message}
              helperText={errors.kebele?.message}
              register={register}
              isLabelBold={true}
              disabled={!isCreatedByUser}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label={t("form.common.houseNumber")}
              name="house_number"
              error={!!errors.house_number?.message}
              helperText={errors.house_number?.message}
              register={register}
              isLabelBold={true}
              disabled={!isCreatedByUser}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label={t("form.common.specificLocation")}
              name="specific_location"
              error={!!errors.specific_location?.message}
              helperText={errors.specific_location?.message}
              register={register}
              isLabelBold={true}
              ref={placesRef}
              placeholder=""
              disabled={!isCreatedByUser}
            />
          </Grid>
        </Grid>
      </Box>
    );
  }
);

StructureDetailAddressSection.displayName = "StructureDetailAddressSection";

export default StructureDetailAddressSection;
